import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../utils/sorting'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { UspConfigurationDealStatusViewResult } from '../../../../models'
import { useDeleteDealStatusMutation } from '../../../../services'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../../ui'
import { usePopover } from '../../../../components/custom-popover'
import Iconify from '../../../../components/iconify'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import Label from '../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))
interface Props {
  data: UspConfigurationDealStatusViewResult[]
  loading: boolean
  setData: Dispatch<SetStateAction<UspConfigurationDealStatusViewResult[]>>
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationDealStatusViewResult>>
}

const DealsDealStatusesTable = ({ data, loading, setData, setOpenAddEditModal, setRowData, setType }: Props) => {
  const [deleteDealStatus, { isSuccess: deleteIsSuccess }] = useDeleteDealStatusMutation()
  const initialOrder = (storageGet('settings_deal_statuses_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('settings_deal_statuses_order_by') || 'DealStatus') as keyof UspConfigurationDealStatusViewResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspConfigurationDealStatusViewResult>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{ dealStatusId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationDealStatusViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('settings_deal_statuses_order', isAsc ? 'desc' : 'asc')
    storageSet('settings_deal_statuses_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationDealStatusViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.dealStatusId) {
      await deleteDealStatus(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.dealStatusId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationDealStatusViewResult[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.dealStatusId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])

  return (
    <>
      <TableContainer sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'DealStatus' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'DealStatus'}
                  direction={orderBy === 'DealStatus' ? order : 'asc'}
                  onClick={createSortHandler('DealStatus')}
                >
                  Name
                  {orderBy === 'DealStatus' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsDeadDeal' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsDeadDeal'}
                  direction={orderBy === 'IsDeadDeal' ? order : 'asc'}
                  onClick={createSortHandler('IsDeadDeal')}
                >
                  Dead Deal
                  {orderBy === 'IsDeadDeal' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsDelivered' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsDelivered'}
                  direction={orderBy === 'IsDelivered' ? order : 'asc'}
                  onClick={createSortHandler('IsDelivered')}
                >
                  Delivered
                  {orderBy === 'IsDelivered' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsUnwound' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsUnwound'}
                  direction={orderBy === 'IsUnwound' ? order : 'asc'}
                  onClick={createSortHandler('IsUnwound')}
                >
                  Unwound
                  {orderBy === 'IsUnwound' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsSystem' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsSystem'}
                  direction={orderBy === 'IsSystem' ? order : 'asc'}
                  onClick={createSortHandler('IsSystem')}
                >
                  System
                  {orderBy === 'IsSystem' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any)?.map((item) => (
              <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  className={classes.tableCellName}
                  onClick={() => {
                    if (!item?.IsSystem) {
                      handleOpen()
                      setRowData(item as any)
                    }
                  }}
                >
                  {item.DealStatus}
                </TableCell>
                <TableCell>{item?.IsDeadDeal ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsDelivered ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsUnwound ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsSystem ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>
                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you wish to delete?'}
      />
      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          disabled={selected?.IsSystem ? true : false}
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={!selected?.IsSystem && selected?.IsActive ? false : true}
          onClick={() => {
            setDeleteConfirmDialog({ dealStatusId: selected.ID?.toString() as string })
            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default DealsDealStatusesTable
